// One active call at a time, rendered into a portal at document.body so it
// stays visible across drawer/tab switches. beforeunload guard warns the user
// before they refresh and kill the SDK mid-call. Provider must live above any
// component that needs to start a call (we wrap the CRM Preview screen).

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import PlivoBrowserCallWidget from "../organisms/calls/PlivoBrowserCallWidget";
import IncomingCallListener from "../organisms/calls/IncomingCallListener";

const ActiveCallContext = createContext({
  activeCall: null,
  startCall: () => {},
  endCall: () => {},
});

// Call statuses the widget reports up via onStatusChange. "Terminal" means
// the call itself is over (audio path closed) — the widget may still be
// visible because the user is writing notes. New call attempts in this
// state should silently replace the stale widget, not block with a toast.
const TERMINAL_STATUSES = new Set(["completed", "failed", "canceled", "rejected"]);

// Master switch for the always-on incoming-call listener.
//
// OFF for now. The whole org shares ONE Plivo SIP endpoint (tgcrmagent…). A
// persistent listener keeps that endpoint registered, so any other call to it
// — including the bridge leg of an OUTBOUND call — fails with "Busy Line
// (Plivo)". With multiple agents on the same endpoint this breaks outbound for
// everyone. Re-enable only after we move to per-agent endpoints (or a clean
// logout/handoff before each outbound dial). All inbound code stays in place;
// flip this to true to turn the feature back on.
//
// Single source of truth, no env var — change is auditable in git and rolls
// out via the standard frontend deploy (same pattern as orgGating's
// CRM_ENABLED_ORGS).
const INCOMING_LISTENER_ENABLED = false;

export function ActiveCallProvider({ children }) {
  const [activeCall, setActiveCall] = useState(null);
  const [mounted, setMounted] = useState(false);
  // Latest widget-reported status, tracked in a ref so startCall can read
  // it synchronously without re-rendering the provider on every status tick.
  const callStatusRef = useRef(null);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!activeCall) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [activeCall]);

  const startCall = useCallback((call) => {
    setActiveCall((current) => {
      // No active call → straightforward start.
      if (!current) {
        callStatusRef.current = null;
        return call;
      }
      // Existing call is already over (audio ended, widget probably still
      // hanging around for the notes form). Silently replace — the previous
      // widget unmounts and a fresh one mounts for the new call.
      if (TERMINAL_STATUSES.has(callStatusRef.current)) {
        callStatusRef.current = null;
        return call;
      }
      // Otherwise the old call is genuinely live (ringing / connecting /
      // in-progress) — block the new one with the same toast as before.
      toast.error("A call is already in progress. End it before starting another.");
      return current;
    });
  }, []);

  const endCall = useCallback(() => {
    callStatusRef.current = null;
    setActiveCall(null);
  }, []);

  const handleStatusChange = useCallback((status) => {
    callStatusRef.current = status;
  }, []);

  const widget = activeCall ? (
    <PlivoBrowserCallWidget
      key={activeCall.callLogId}
      leadId={activeCall.leadId}
      callLogId={activeCall.callLogId}
      to={activeCall.to}
      leadTitle={activeCall.leadTitle}
      testMode={!!activeCall.testMode}
      onClose={endCall}
      onStatusChange={handleStatusChange}
    />
  ) : null;

  // Always-on incoming listener — gated by INCOMING_LISTENER_ENABLED (see top
  // of file). Paused while an outbound call is live so the two clients don't
  // both register the shared SIP endpoint at once.
  const listener = INCOMING_LISTENER_ENABLED
    ? <IncomingCallListener paused={!!activeCall} />
    : null;

  return (
    <ActiveCallContext.Provider value={{ activeCall, startCall, endCall }}>
      {children}
      {mounted && listener && createPortal(listener, document.body)}
      {mounted && widget && createPortal(widget, document.body)}
    </ActiveCallContext.Provider>
  );
}

export function useActiveCall() {
  return useContext(ActiveCallContext);
}
